// src/routes/Home.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";

import Layout from "../components/Layout";
import ProductCard from "../components/ProductCard";
import FilterBar, { applyFilters } from "../components/FilterBar";
import StudioScroll from "../components/StudioScroll";
import useReveal from "../hooks/useReveal";
import { useLang } from "../context/LanguageContext";
import {
  getProducts,
  getCategories,
  displayName,
  productCode,
} from "../services/db";

gsap.registerPlugin(ScrollTrigger);

const HOME_PAGE_SIZE = 8;

/* Real shades from the LaBina Farbkarte — used for the marquee before
   Firestore loads, and as a fallback if no product has a colour set. */
const FALLBACK_SHADES = [
  { hex: "#D8B98C", nameSq: "Bionde" },
  { hex: "#9C7A55", nameSq: "Bionde e errët" },
  { hex: "#B5702A", nameSq: "Kafe e çelët" },
  { hex: "#6E4A24", nameSq: "Kafe mesatare" },
  { hex: "#3B2B1E", nameSq: "Kafe e errët" },
  { hex: "#241A14", nameSq: "Kafe e zezë" },
  { hex: "#8C8C8C", nameSq: "Gri e çelët" },
  { hex: "#1A1A1A", nameSq: "E zezë intensive" },
  { hex: "#B87C7C", nameSq: "Rozë e vjetër" },
  { hex: "#E8618F", nameSq: "Rozë pudër" },
  { hex: "#D4295A", nameSq: "E kuqe trëndafili" },
  { hex: "#E33C22", nameSq: "E kuqe Mars" },
  { hex: "#B01836", nameSq: "E kuqe qershie" },
  { hex: "#6E1436", nameSq: "Bordo" },
  { hex: "#C1341F", nameSq: "Terrakotë" },
  { hex: "#E5B2A0", nameSq: "Nuancë lëkure" },
  { hex: "#1B5E3A", nameSq: "Jeshile ulliri" },
  { hex: "#1F3A73", nameSq: "Blu mesnate" },
];

/* Straight from the LaBina certificate — no invented marketing numbers. */
const FACTS = [
  { value: "2004", key: "home.facts.since" },
  { value: "18", key: "home.facts.params" },
  { value: "0", key: "home.facts.iron" },
  { value: "100%", key: "home.facts.preservatives" },
];

export default function Home() {
  const { t, lang } = useLang();

  const heroRef = useRef(null);
  const marqueeRef = useRef(null);
  const splitRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sort, setSort] = useState("code");
  const [shown, setShown] = useState(HOME_PAGE_SIZE);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, c] = await Promise.all([getProducts(), getCategories()]);
        if (!alive) return;
        setProducts(p.filter((x) => x.active !== false));
        setCategories(c);
      } catch (err) {
        console.error("Home load failed", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const shades = useMemo(() => {
    const fromDb = products
      .filter((p) => p.hex)
      .map((p) => ({
        id: p.id,
        hex: p.hex,
        nameSq: displayName(p, lang),
        code: productCode(p),
      }));
    return fromDb.length >= 6 ? fromDb : FALLBACK_SHADES;
  }, [products, lang]);

  const filtered = useMemo(
    () => applyFilters(products, { search, categoryId, sort }),
    [products, search, categoryId, sort],
  );

  const visible = filtered.slice(0, shown);
  const visibleCount = visible.length;
  const remaining = filtered.length - visibleCount;

  useEffect(() => {
    setShown(HOME_PAGE_SIZE);
  }, [search, categoryId, sort]);

  const scope = useReveal({ deps: [loading, visibleCount] });

  /* ---------------- hero ---------------- */
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });

      tl.from(".hero-line span", {
        yPercent: 108,
        duration: 1.05,
        stagger: 0.08,
      })
        .from(
          ".hero-meta",
          { opacity: 0, y: 16, duration: 0.7, stagger: 0.07 },
          "-=0.7",
        )
        .from(
          ".hero-rule",
          { scaleX: 0, duration: 1, ease: "power3.inOut" },
          "-=0.8",
        );

      // decorative drift only — the text stays readable the whole way
      gsap.to(".hero-inner", {
        yPercent: -12,
        opacity: 0.3,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 0.8,
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  /* Infinite marquee. Track is duplicated and wrapped at -50%, so it loops
     seamlessly instead of snapping back. */
  useLayoutEffect(() => {
    const el = marqueeRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tween = gsap.to(".marquee-track", {
        xPercent: -50,
        duration: 46,
        ease: "none",
        repeat: -1,
      });

      // scroll velocity nudges the speed, then it settles back
      ScrollTrigger.create({
        trigger: el,
        start: "top bottom",
        end: "bottom top",
        onUpdate: (self) => {
          const boost = 1 + Math.min(Math.abs(self.getVelocity()) / 2400, 2.2);
          gsap.to(tween, { timeScale: boost, duration: 0.3, overwrite: true });
          gsap.to(tween, { timeScale: 1, duration: 1.6, delay: 0.35 });
        },
      });
    }, marqueeRef);

    return () => ctx.revert();
  }, [shades.length]);

  /* Statement lines brighten from near-invisible as they cross the viewport. */
  useLayoutEffect(() => {
    const el = splitRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray(".statement").forEach((node) => {
        gsap.fromTo(
          node,
          { opacity: 0.14 },
          {
            opacity: 1,
            ease: "none",
            scrollTrigger: {
              trigger: node,
              start: "top 84%",
              end: "top 45%",
              scrub: 0.6,
            },
          },
        );
      });
    }, splitRef);

    return () => ctx.revert();
  }, []);

  const marqueeItems = [...shades, ...shades];

  return (
    <Layout padTop={false}>
      <div ref={scope}>
        {/* ---------------- hero ---------------- */}
        <Hero ref={heroRef}>
          <HeroInner className="hero-inner">
            <HeroTop className="hero-meta">
              <span>Kaida Beauty Studio</span>
              <span>Kosovë</span>
            </HeroTop>

            <Display>
              <Line className="hero-line">
                <span>{t("home.heroLine1")}</span>
              </Line>
              <Line className="hero-line">
                <span>{t("home.heroLine2")}</span>
              </Line>
              <Line className="hero-line">
                <span>
                  {t("home.heroLine3a")} <Accent>{t("home.heroLine3b")}</Accent>
                </span>
              </Line>
            </Display>

            <Rule className="hero-rule" />

            <HeroFoot>
              <HeroText className="hero-meta">{t("home.heroLead")}</HeroText>

              <HeroActions className="hero-meta">
                <Primary to="/produktet">
                  {t("home.heroShop")} <FiArrowRight />
                </Primary>
                <Ghost href="#studio">
                  {t("home.heroStudio")} <FiArrowUpRight />
                </Ghost>
              </HeroActions>
            </HeroFoot>
          </HeroInner>
        </Hero>

        {/* ---------------- marquee ---------------- */}
        <Marquee ref={marqueeRef} aria-hidden="true">
          <MarqueeTrack className="marquee-track">
            {marqueeItems.map((s, i) => (
              <MarqueeItem key={`${s.hex}-${i}`}>
                <Dot $hex={s.hex} />
                <span>{s.nameSq}</span>
                {s.code && <em>{s.code}</em>}
              </MarqueeItem>
            ))}
          </MarqueeTrack>
        </Marquee>

        {/* ---------------- statement ---------------- */}
        <Statement ref={splitRef}>
          <StatementInner>
            <StatementLine className="statement">
              {t("home.statement1")}
            </StatementLine>
            <StatementLine className="statement">
              {t("home.statement2")}
            </StatementLine>

            <Facts data-reveal-stagger>
              {FACTS.map((f) => (
                <Fact key={f.key} className="reveal">
                  <FactValue>{f.value}</FactValue>
                  <FactLabel>{t(f.key)}</FactLabel>
                </Fact>
              ))}
            </Facts>
          </StatementInner>
        </Statement>

        {/* ---------------- studio (the one pinned section) ---------------- */}
        <StudioScroll />

        {/* ---------------- shop ---------------- */}
        <Shop>
          <ShopHead>
            <div>
              <Eyebrow className="reveal">{t("home.shopEyebrow")}</Eyebrow>
              <ShopTitle className="reveal">{t("home.shopTitle")}</ShopTitle>
            </div>
            <SeeAll to="/produktet" className="reveal">
              {t("home.seeAll")} <FiArrowRight />
            </SeeAll>
          </ShopHead>

          <FilterWrap className="reveal">
            <FilterBar
              search={search}
              onSearch={setSearch}
              categories={categories}
              categoryId={categoryId}
              onCategory={setCategoryId}
              sort={sort}
              onSort={setSort}
              resultCount={loading ? undefined : filtered.length}
            />
          </FilterWrap>

          {loading ? (
            <Grid>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} />
              ))}
            </Grid>
          ) : visible.length ? (
            <>
              <Grid data-reveal-stagger>
                {visible.map((p) => (
                  <ProductCard key={p.id} product={p} className="reveal" />
                ))}
              </Grid>

              {remaining > 0 && (
                <MoreWrap>
                  <MoreBtn onClick={() => setShown((n) => n + HOME_PAGE_SIZE)}>
                    {t("common.showMore")}
                    <MoreCount>
                      +{Math.min(remaining, HOME_PAGE_SIZE)}
                    </MoreCount>
                  </MoreBtn>
                  <MoreNote>
                    {t("common.ofProducts", {
                      shown: visibleCount,
                      total: filtered.length,
                    })}
                  </MoreNote>
                </MoreWrap>
              )}

              {remaining === 0 && filtered.length > HOME_PAGE_SIZE && (
                <MoreWrap>
                  <MoreNote>
                    {t("common.allShown", { count: filtered.length })}
                  </MoreNote>
                </MoreWrap>
              )}
            </>
          ) : (
            <EmptyState className="reveal">
              <p>{t("home.noResults")}</p>
              <button
                onClick={() => {
                  setSearch("");
                  setCategoryId("");
                }}
              >
                {t("common.clearFilters")}
              </button>
            </EmptyState>
          )}
        </Shop>

        {/* ---------------- cta ---------------- */}
        <Cta>
          <CtaInner>
            <CtaTitle className="reveal">
              {t("home.ctaTitle1")}
              <br />
              {t("home.ctaTitle2")}
            </CtaTitle>
            <CtaText className="reveal">{t("home.ctaText")}</CtaText>
            <CtaActions className="reveal">
              <Primary to="/kontakt">
                {t("home.ctaButton")} <FiArrowRight />
              </Primary>
            </CtaActions>
          </CtaInner>
        </Cta>
      </div>
    </Layout>
  );
}

/* ------------------------------------------------------------------ */
/*  hero — oversized type, extreme whitespace, one accent               */
/* ------------------------------------------------------------------ */

const Hero = styled.section`
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  padding: calc(${({ theme }) => theme.layout.navHeight} + 40px)
    ${({ theme }) => theme.layout.gutter} clamp(40px, 6vh, 80px);
`;

const HeroInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const HeroTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: clamp(40px, 8vh, 90px);
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.muted};
`;

const Display = styled.h1`
  margin: 0;
  font-size: clamp(3.1rem, 11.5vw, 10.5rem);
  line-height: 0.94;
  letter-spacing: -0.058em;
  font-weight: ${({ theme }) => theme.weight.medium};
`;

const Line = styled.span`
  display: block;
  /* The mask clips anything outside the box, and a line-height under 1 puts
     descenders (g, j, q, ç) outside it. Pad the box to fit the tails, then
     pull the same amount back off the margin so line spacing is unchanged. */
  overflow: hidden;
  padding-bottom: 0.18em;
  margin-bottom: -0.18em;

  span {
    display: block;
  }
`;

const Accent = styled.em`
  font-style: normal;
  color: ${({ theme }) => theme.color.brand};
`;

const Rule = styled.div`
  height: 1px;
  width: 100%;
  margin: clamp(36px, 6vh, 64px) 0 32px;
  background: ${({ theme }) => theme.color.line};
  transform-origin: left;
`;

const HeroFoot = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;

  ${({ theme }) => theme.down.md} {
    flex-direction: column;
    align-items: flex-start;
    gap: 28px;
  }
`;

const HeroText = styled.p`
  margin: 0;
  max-width: 42ch;
  font-size: ${({ theme }) => theme.size.lg};
  color: ${({ theme }) => theme.color.body};

  sup {
    font-size: 0.55em;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;
`;

const Primary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  padding: 0 30px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.ink};
  color: #fff;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  transition:
    background 0.3s,
    transform 0.3s ${({ theme }) => theme.ease.out};

  svg {
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }

  &:hover {
    background: ${({ theme }) => theme.color.brand};
    transform: translateY(-2px);
    svg {
      transform: translateX(4px);
    }
  }
`;

const Ghost = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  padding: 0 30px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.color.line};
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.ink};
  transition: all 0.3s ${({ theme }) => theme.ease.out};

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
    background: ${({ theme }) => theme.color.bgSoft};
  }
`;

/* ------------------------------------------------------------------ */
/*  marquee                                                            */
/* ------------------------------------------------------------------ */

const Marquee = styled.div`
  overflow: hidden;
  padding: 22px 0;
  border-block: 1px solid ${({ theme }) => theme.color.lineSoft};
  user-select: none;
`;

const MarqueeTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 44px;
  width: max-content;
  will-change: transform;
`;

const MarqueeItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.color.body};

  em {
    font-style: normal;
    font-size: ${({ theme }) => theme.size.xs};
    color: ${({ theme }) => theme.color.muted};
  }
`;

const Dot = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $hex }) => $hex};
  box-shadow: inset 0 0 0 1px rgba(12, 10, 9, 0.1);
  flex-shrink: 0;
`;

/* ------------------------------------------------------------------ */
/*  statement                                                          */
/* ------------------------------------------------------------------ */

const Statement = styled.section`
  padding: ${({ theme }) => theme.layout.section}${({ theme }) =>
      theme.layout.gutter};
`;

const StatementInner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const StatementLine = styled.p`
  margin: 0 0 0.35em;
  max-width: 22ch;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(1.7rem, 4.2vw, 3.6rem);
  line-height: 1.14;
  letter-spacing: -0.04em;
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.ink};

  &:last-of-type {
    color: ${({ theme }) => theme.color.brand};
    max-width: 20ch;
  }
`;

const Facts = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-top: clamp(56px, 9vw, 110px);
  padding-top: 40px;
  border-top: 1px solid ${({ theme }) => theme.color.line};

  ${({ theme }) => theme.down.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }
`;

const Fact = styled.div``;

const FactValue = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: clamp(2rem, 3.4vw, 3.2rem);
  line-height: 1.08;
  letter-spacing: -0.045em;
  color: ${({ theme }) => theme.color.ink};
`;

const FactLabel = styled.p`
  margin: 12px 0 0;
  max-width: 20ch;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.muted};
`;

/* ------------------------------------------------------------------ */
/*  shop                                                               */
/* ------------------------------------------------------------------ */

const Shop = styled.section`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.layout.section}${({ theme }) =>
      theme.layout.gutter};
`;

const ShopHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;

  ${({ theme }) => theme.down.md} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 14px;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const ShopTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3.4rem);
  letter-spacing: -0.045em;
`;

const SeeAll = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 5px;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.ink};
  border-bottom: 1px solid ${({ theme }) => theme.color.line};
  transition:
    color 0.25s,
    border-color 0.25s;

  &:hover {
    color: ${({ theme }) => theme.color.brand};
    border-color: ${({ theme }) => theme.color.brand};
  }
`;

const FilterWrap = styled.div`
  margin-bottom: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.color.lineSoft};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
  ${({ theme }) => theme.down.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const Skeleton = styled.div`
  aspect-ratio: 1 / 1.28;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.color.bgSoft} 30%,
    ${({ theme }) => theme.color.lineSoft} 50%,
    ${({ theme }) => theme.color.bgSoft} 70%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s infinite linear;

  @keyframes shimmer {
    to {
      background-position: -220% 0;
    }
  }
`;

const MoreWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 48px;
`;

const MoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 0 16px 0 30px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.color.line};
  background: ${({ theme }) => theme.color.bg};
  color: ${({ theme }) => theme.color.ink};
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  transition: all 0.28s ${({ theme }) => theme.ease.out};

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
    background: ${({ theme }) => theme.color.ink};
    color: #fff;
    transform: translateY(-2px);
  }
`;

const MoreCount = styled.span`
  padding: 5px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.bgSoft};
  color: ${({ theme }) => theme.color.body};
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.semi};
  transition:
    background 0.28s,
    color 0.28s;

  ${MoreBtn}:hover & {
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
  }
`;

const MoreNote = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
`;

const EmptyState = styled.div`
  padding: 70px 0;
  text-align: center;
  color: ${({ theme }) => theme.color.muted};

  button {
    margin-top: 10px;
    color: ${({ theme }) => theme.color.brand};
    font-weight: ${({ theme }) => theme.weight.semi};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

/* ------------------------------------------------------------------ */
/*  cta                                                                */
/* ------------------------------------------------------------------ */

const Cta = styled.section`
  border-top: 1px solid ${({ theme }) => theme.color.lineSoft};
  padding: ${({ theme }) => theme.layout.section}${({ theme }) =>
      theme.layout.gutter};
`;

const CtaInner = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  font-size: clamp(2.2rem, 6vw, 5rem);
  line-height: 1.04;
  letter-spacing: -0.05em;
`;

const CtaText = styled.p`
  margin: 28px 0 34px;
  max-width: 46ch;
  font-size: ${({ theme }) => theme.size.lg};
  color: ${({ theme }) => theme.color.body};
`;

const CtaActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;
